/**
 * Acting on every conversation in a folded group: the two handlers the list's
 * group menu calls, and the question a group delete asks first.
 *
 * Kept out of `EmailSection`, which is already the biggest file on this desk.
 * The section only hands the handlers down and renders `confirm`.
 */
import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { useT } from "../../../i18n/I18nProvider";
import type { MailThread } from "../../../lib/mail";
import { bulkDeleteThreads, bulkPatchThreads, type BulkThreadPatch } from "../../../lib/mailActions";
import { useToast } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { summarizeMailGroup, type MailGroup } from "./grouped";

/** The same words the single row's menu uses for the same change. */
const STATUS_TOAST: Record<NonNullable<BulkThreadPatch["status"]>, string> = {
  archived: "mail.archived",
  spam: "mail.spammed",
  open: "mail.unarchived",
};

export function useThreadBulk({
  loadList,
  refresh,
  thread,
  setThread,
  navigate,
}: {
  /** Reloads the folder and answers with what is in it now. */
  loadList: () => Promise<MailThread[]>;
  refresh: () => void;
  /** The conversation the reading pane holds, which may be one of the group. */
  thread: MailThread | null;
  setThread: Dispatch<SetStateAction<MailThread | null>>;
  navigate: (to: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [deleting, setDeleting] = useState<MailGroup | null>(null);

  const patchMany = useCallback(
    async (ids: string[], patch: BulkThreadPatch) => {
      try {
        await bulkPatchThreads(ids, patch);
        // The open conversation follows its group. The pane would otherwise
        // keep offering "Archive" on a thread the menu has just archived.
        setThread((prev) => (prev && ids.includes(prev.id) ? { ...prev, ...patch } : prev));
        await loadList();
        refresh();
        if (patch.status) toast(t(STATUS_TOAST[patch.status]));
      } catch {
        toast(t("common.error"));
      }
    },
    [loadList, refresh, setThread, t, toast],
  );

  /** Deleting the group that holds the open conversation moves the pane on,
   *  exactly as deleting that one conversation would. */
  async function removeGroup(group: MailGroup) {
    const ids = group.rows.map((th) => th.id);
    await bulkDeleteThreads(ids);
    const next = (await loadList()).filter((r) => !ids.includes(r.id));
    refresh();
    toast(t("mail.deleted"));
    if (!thread || !ids.includes(thread.id)) return;
    setThread(null);
    navigate(next[0] ? `/email/${next[0].id}` : "/email");
  }

  const count = deleting ? String(deleting.rows.length) : "";
  const confirm = deleting ? (
    <ConfirmModal
      title={t("mail.group.deleteTitle", { count })}
      body={t("mail.group.deleteBody", { count, who: summarizeMailGroup(deleting).who })}
      confirmLabel={t("mail.group.deleteAll", { count })}
      cancelLabel={t("common.cancel")}
      onConfirm={async () => {
        // Cleared after, not before, for the reason the single delete gives:
        // a dialog that vanishes before the rows do reads as a finished delete.
        try {
          await removeGroup(deleting);
        } catch {
          toast(t("common.error"));
        }
        setDeleting(null);
      }}
      onClose={() => setDeleting(null)}
    />
  ) : null;

  return { patchMany, askDeleteMany: setDeleting, confirm };
}
